import { Button, ErrorCard } from '@starsoft/common/components';
import {
  CommonModalsKey,
  KYCStatus,
  UserPaymentDetailsStatus,
} from '@starsoft/common/enums';
import {
  useInfiniteScroll,
  useKycMeByOriginIdWithOrigin,
  useTransactionsNavigation,
  useTranslation,
  useUserPaymentTypeIdByCoinId,
  useWithdrawSearchParams,
} from '@starsoft/common/hooks';
import { Nullable } from '@starsoft/common/interfaces';
import { GenericError, UserPaymentDetails } from '@starsoft/common/models';
import { AxiosError } from 'axios';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { useUserPaymentDetailsMe } from '../../../../../api';
import { WithdrawFormCreateDto } from '../../../../../dto/withdrawals/baseWithdrawalInputDto/withdraw-create-form.dto';
import UserPaymentDetailsCard from './Card';
import UserPaymentDetailsKycCheck from './KycCheck';
import styles from './styles.module.scss';

/**
 * UserPaymentDetailsContent Component
 * This component is responsible for rendering the user payment details content.
 * It handles the display of user payment details, KYC status, and provides functionality for adding new user payment details.
 *
 * @returns {JSX.Element} The rendered component displaying user payment details or an error card if an error occurs.
 *
 * @example
 * // Usage example:
 * <UserPaymentDetailsContent />
 */
export default function UserPaymentDetailsContent() {
  const { t } = useTranslation('common');
  const isFirstMount: MutableRefObject<boolean> = useRef(true);
  const { setValue, watch } = useFormContext<WithdrawFormCreateDto>();
  const userPaymentDetailSelectedAccount = watch('paymentDetailsId');
  const { navigateToTransactionModal } = useTransactionsNavigation();

  const {
    kyc,
    error: errorKyc,
    refetch: refetchKyc,
    isPending: isPendingKyc,
  } = useKycMeByOriginIdWithOrigin();
  const paymentType: Nullable<string> = useUserPaymentTypeIdByCoinId();
  const {
    userPaymentDetails,
    error: errorUserPaymentDetailsMe,
    refetch: refetchUserPaymentDetailsMe,
    isLoading: isLoadingUserPaymentDetailsMe,
    hasNextPage: hasNextPageUserPaymentDetailsMe,
    fetchNextPage: fetchNextPageUserPaymentDetailsMe,
    isFetchingNextPage: isFetchingNextPageUserPaymentDetailsMe,
  } = useUserPaymentDetailsMe({
    paymentType,
    type: 'infinity',
    enabled: !!kyc,
  });

  console.log(isLoadingUserPaymentDetailsMe, 'isLoadingUserPaymentDetailsMe');
  console.log(isPendingKyc, 'isPendingKyc');

  const error: AxiosError<GenericError> = errorUserPaymentDetailsMe || errorKyc;
  const isLoading: boolean = isLoadingUserPaymentDetailsMe || isPendingKyc;
  const isWithoutUserPaymentDetails: boolean =
    !isLoading && (!userPaymentDetails || userPaymentDetails?.length == 0);

  const { ref } = useInfiniteScroll({
    hasMore: hasNextPageUserPaymentDetailsMe,
    isLoading: isLoading || isFetchingNextPageUserPaymentDetailsMe,
    loadMore: fetchNextPageUserPaymentDetailsMe,
  });

  const { handleSetWithdrawSearchParams } = useWithdrawSearchParams();

  /**
   * onButtonClick Function
   * This function handles the button click event.
   * It navigates to the deposit modal if KYC is not verified, otherwise sets the withdraw search params.
   */
  function onButtonClick() {
    if (!kyc || kyc?.status !== KYCStatus.Verified) {
      navigateToTransactionModal(CommonModalsKey.Deposit);
      return;
    }

    handleSetWithdrawSearchParams('user-payment', 'new');
  }

  /**
   * handleSelectAccount Function
   * This function handles the selection of a user account.
   * It sets the payment details ID in the form context based on the account status.
   *
   * @param {UserPaymentDetails} userAccount - The user account object containing payment details.
   *
   * @example
   * handleSelectAccount({ id: 1, status: UserPaymentDetailsStatus.Verified });
   */
  const handleSelectAccount = useCallback(
    (userAccount: UserPaymentDetails) => {
      switch (userAccount?.status) {
        case UserPaymentDetailsStatus.Created:
        case UserPaymentDetailsStatus.Verified:
          setValue('paymentDetailsId', userAccount?.id as bigint, {
            shouldDirty: true,
          });
          break;
        case UserPaymentDetailsStatus.Rejected:
          handleSetWithdrawSearchParams(
            'user-payment',
            userAccount?.id?.toString(),
          );
      }
    },
    [handleSetWithdrawSearchParams, setValue],
  );

  /**
   * handleError Function
   * This function handles the error state by refetching the user payment details and KYC data.
   */
  function handleError() {
    if (errorUserPaymentDetailsMe) {
      refetchUserPaymentDetailsMe();
    }

    if (errorKyc) {
      refetchKyc();
    }
  }

  /**
   * getButtonLabel Function
   * This function returns the appropriate button label based on the KYC status and user payment details.
   *
   * @returns {string} The button label.
   *
   * @example
   * const label = getButtonLabel();
   */
  function getButtonLabel(): string {
    if (!kyc) {
      return 'withdraw:create_kyc_button';
    } else if (!!kyc && kyc?.status !== KYCStatus.Verified) {
      return 'withdraw:verify_kyc_button';
    }

    return isWithoutUserPaymentDetails
      ? 'create_user_account_label'
      : 'add_user_account_label';
  }

  /**
   * getTitle Function
   * This function returns the appropriate title based on the KYC status.
   *
   * @returns {string} The title.
   *
   * @example
   * const title = getTitle();
   */
  function getTitle(): string {
    if (!kyc) {
      return 'withdraw:create_kyc_button';
    } else if (!!kyc && kyc?.status !== KYCStatus.Verified) {
      return 'withdraw:verify_kyc_button';
    }

    return 'withdraw_account_select_label';
  }

  /**
   * Cards Memo
   * This memoized array of JSX elements represents the user payment details cards.
   *
   * @returns {JSX.Element[]} The array of user payment details cards.
   *
   * @example
   * const cards = Cards;
   */
  const Cards: JSX.Element[] = useMemo(
    () =>
      userPaymentDetails?.map((userPaymentDetail, index) => (
        <UserPaymentDetailsCard
          position={index + 1}
          selected={
            String(userPaymentDetailSelectedAccount) ==
            String(userPaymentDetail?.id)
          }
          onClick={handleSelectAccount}
          userPaymentDetails={userPaymentDetail}
          key={`user-payment-detail-${userPaymentDetail?.id}`}
        />
      )),
    [userPaymentDetails, handleSelectAccount, userPaymentDetailSelectedAccount],
  );

  /**
   * Skeletons Memo
   * This memoized array of JSX elements represents the skeleton loading state for user payment details cards.
   *
   * @returns {JSX.Element[]} The array of skeleton loading state cards.
   *
   * @example
   * const skeletons = Skeletons;
   */
  const Skeletons: JSX.Element[] = useMemo(
    () =>
      Array.from({ length: 5 }).map((_, index) => (
        <UserPaymentDetailsCard
          key={`user-payment-detail-skeleton-${index}`}
          isLoading
        />
      )),
    [],
  );

  /**
   * onFetchUserPaymentDetails Function
   * This function handles the fetching of user payment details on component mount.
   * It sets the withdraw search params if there are no user payment details and it is the first mount.
   */
  function onFetchUserPaymentDetails() {
    if (!isWithoutUserPaymentDetails || !isFirstMount.current || !kyc) {
      return;
    }

    isFirstMount.current = false;
    handleSetWithdrawSearchParams('user-payment', 'new');
  }

  // useEffect hook to execute onFetchUserPaymentDetails when the component mounts
  useEffect(onFetchUserPaymentDetails, [
    handleSetWithdrawSearchParams,
    isWithoutUserPaymentDetails,
    kyc,
  ]);

  // console.log(userPaymentDetails, 'userPaymentDetails');
  // console.log(isWithoutUserPaymentDetails, 'isWithoutUserPaymentDetails');
  // console.log(kyc, 'kyc');
  console.log(isLoading, 'isLoading');

  // If there is an error, render the ErrorCard component with the error and refetch function
  if (error) {
    return <ErrorCard error={error} refetch={handleError} />;
  }

  // Render the user payment details content
  return (
    <div className={styles.container}>
      <div className={styles.container__heading}>
        {isLoading ? (
          <div className={styles.container__heading__skeleton} />
        ) : (
          t(getTitle())
        )}
      </div>
      {((isWithoutUserPaymentDetails ||
        (!kyc && !isLoading) ||
        (!!kyc && kyc?.status !== KYCStatus.Verified)) && (
        <UserPaymentDetailsKycCheck />
      )) || (
        <div className={styles.container__cards} ref={ref}>
          {isLoading ? Skeletons : Cards}
          {isFetchingNextPageUserPaymentDetailsMe && Skeletons}
        </div>
      )}
      <div className={styles.container__padded}>
        <Button
          dotted
          onClick={onButtonClick}
          type="button"
          isSkeleton={isLoading}
          skeletonDefault
        >
          <i className="fa-solid fa-plus" />
          {t(getButtonLabel())}
        </Button>
      </div>
    </div>
  );
}
